import { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { extractApiError } from "../lib/errors";
import { Lock, LogIn } from "lucide-react";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (user) nav("/admin", { replace: true });
  }, [user, nav]);

  if (loading) {
    return (
      <main className="bg-canvas min-h-[70vh] flex items-center justify-center text-[#C5D1EB] text-sm">
        Loading…
      </main>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      const to = loc.state?.from?.pathname || "/admin";
      nav(to, { replace: true });
    } catch (er) {
      setErr(extractApiError(er, "Login failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="bg-canvas min-h-[80vh] flex items-center"
    >
      <div className="max-w-md w-full mx-auto px-5 sm:px-8 py-16">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-[#C5001A] text-white">
          <Lock size={18} />
        </div>
        <h1 className="mt-6 font-display text-3xl sm:text-4xl text-white tracking-tight">
          Admin Studio
        </h1>
        <p className="mt-2 text-sm text-[#C5D1EB]">
          Staff sign-in for the broadcast control room.
        </p>

        <form
          onSubmit={submit}
          className="mt-10 bg-[#1F2833] border border-[#C5D1EB]/15 p-7 space-y-5"
          data-testid="admin-login-form"
        >
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Email
            </label>
            <input
              data-testid="login-email"
              className="brand-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
              Password
            </label>
            <input
              data-testid="login-password"
              className="brand-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {err && (
            <p
              data-testid="login-error"
              className="text-sm text-[#E31C3D]"
            >
              {err}
            </p>
          )}

          <button
            data-testid="login-submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] disabled:opacity-60 text-white px-6 py-3 text-sm transition-colors"
          >
            <LogIn size={15} />
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}
